import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import useRetryFetch from "../../../hooks/useRetryFetch";
import useAuth from "../../../hooks/useAuth";
import {
  UserTrait,
  UpdateTraitRequest,
  TraitCategory,
  type AddEvidenceRequest,
  type EvidenceRelationship,
} from "../../../api/userTraits";
import { listTakeaways, type UserTakeaway } from "../../../api/userTakeaways";
import { getUserPrescriptions } from "../../../api/prescriptions";
import { config } from "../../../Environment";
import { calibrateTraitBatch, assistTraitDefinition } from "../../../api/traitAI";
import {
  getLevelConfig,
  getLevelLabel,
  getHierarchyLabel,
  countEvidence,
} from "../utils/traitUtils";
import AssessmentLevelInput from "./AssessmentLevelInput";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../ui/sheet";
import { Badge } from "../../ui/badge";
import { Separator } from "../../ui/separator";
import { ScrollArea } from "../../ui/scroll-area";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { Input } from "../../ui/input";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../../ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/command";
import { ChevronsUpDown } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../ui/alert-dialog";
import { liftEntity } from "../../../api/mentalModels";
import {
  Brain,
  Layers,
  GitBranch,
  Target,
  Trash2,
  Plus,
  X,
  Pencil,
  Check,
  Lightbulb,
  BookOpen,
  MessageSquare,
  Zap,
  FileText,
  Orbit,
  Tag,
  Wand2,
  Search,
  Loader2,
  Circle,
} from "lucide-react";
import { TraitIcon } from "../utils/traitIcon";
import { toast } from "sonner";

// ─── Types ──────────────────────────────────────────────────────────────────

interface TraitDetailPanelProps {
  trait: UserTrait | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (id: string, data: UpdateTraitRequest) => Promise<void>;
  onAssess: (id: string, level: number, note?: string) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onAddEvidence?: (id: string, data: AddEvidenceRequest) => Promise<void>;
  onDeleteEvidence?: (id: string, evidenceId: string) => Promise<void>;
  spaceColor?: string;
  spaces?: { _id: string; name: string }[];
  spaceId?: string;
  readOnly?: boolean;
}

// ─── Evidence type icon map ─────────────────────────────────────────────────

const EVIDENCE_ICONS: Record<string, React.ElementType> = {
  insight: Lightbulb,
  engagement: BookOpen,
  reflection: MessageSquare,
  action_item: Zap,
  note: FileText,
};

// ─── Category badges ────────────────────────────────────────────────────────

const CATEGORY_STYLES: Record<TraitCategory, { bg: string; text: string }> = {
  strength: { bg: "bg-amber-50", text: "text-amber-700" },
  value: { bg: "bg-rose-50", text: "text-rose-700" },
  aspiration: { bg: "bg-indigo-50", text: "text-indigo-700" },
  skill: { bg: "bg-emerald-50", text: "text-emerald-700" },
};

// ─── Hierarchy icon map ─────────────────────────────────────────────────────

const HIERARCHY_ICON_MAP: Record<string, React.ElementType> = {
  domain: Layers,
  capability: GitBranch,
  behavior: Target,
};

// ─── Component ──────────────────────────────────────────────────────────────

const TraitDetailPanel: React.FC<TraitDetailPanelProps> = ({
  trait,
  isOpen,
  onClose,
  onUpdate,
  onAssess,
  onDelete,
  onAddEvidence,
  onDeleteEvidence,
  spaceColor = "#6366f1",
  spaces,
  spaceId,
  readOnly = false,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const retryFetch = useRetryFetch();

  // Local editing state
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [definitionDraft, setDefinitionDraft] = useState("");
  const [opportunitiesDraft, setOpportunitiesDraft] = useState("");
  const [examplesDraft, setExamplesDraft] = useState<string[]>([]);
  const [newExample, setNewExample] = useState("");
  const [tagsDraft, setTagsDraft] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [saving, setSaving] = useState(false);

  // Evidence add form state
  const [newEvidenceNote, setNewEvidenceNote] = useState("");
  const [newEvidenceType, setNewEvidenceType] = useState<AddEvidenceRequest["type"]>("insight");
  const [newEvidenceRel, setNewEvidenceRel] = useState<EvidenceRelationship>("develops");
  const [addingEvidence, setAddingEvidence] = useState(false);

  // Linked entity state for evidence
  const [selectedActionItemId, setSelectedActionItemId] = useState("");
  const [selectedInsightId, setSelectedInsightId] = useState("");
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState("");
  const [selectedProcessId, setSelectedProcessId] = useState("");
  const [actionItems, setActionItems] = useState<UserTakeaway[]>([]);
  const [insights, setInsights] = useState<UserTakeaway[]>([]);
  const [prescriptions, setPrescriptions] = useState<{ _id: string; name: string }[]>([]);
  const [linkedProcesses, setLinkedProcesses] = useState<{ _id: string; prescriptionName: string; engagementName?: string; status: string }[]>([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [openInsightPicker, setOpenInsightPicker] = useState(false);
  const [openActionPicker, setOpenActionPicker] = useState(false);
  const [openActivityPicker, setOpenActivityPicker] = useState(false);
  const [openEngagementPicker, setOpenEngagementPicker] = useState(false);

  // AI features state
  const [levelDescriptions, setLevelDescriptions] = useState<Record<number, string>>({});
  const [loadingLevelDescs, setLoadingLevelDescs] = useState(false);
  const [defAssisting, setDefAssisting] = useState(false);

  // Sync local state when trait changes
  useEffect(() => {
    if (trait) {
      setNameDraft(trait.name || "");
      setDefinitionDraft(trait.personalDefinition || "");
      setOpportunitiesDraft(trait.developmentOpportunities || "");
      setExamplesDraft(trait.personalExamples || []);
      setTagsDraft(trait.tags || []);
      setEditingName(false);
      setNewExample("");
      setNewTag("");
      setNewEvidenceNote("");
      setNewEvidenceType("insight");
      setNewEvidenceRel("develops");
      setSelectedActionItemId("");
      setSelectedInsightId("");
      setSelectedPrescriptionId("");
      setSelectedProcessId("");
      setLinkedProcesses([]);
      setOpenInsightPicker(false);
      setOpenActionPicker(false);
      setOpenActivityPicker(false);
      setOpenEngagementPicker(false);
    }
  }, [trait?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch linked entities when evidence type changes
  useEffect(() => {
    setSelectedActionItemId("");
    setSelectedInsightId("");
    setSelectedPrescriptionId("");
    setSelectedProcessId("");
    setLinkedProcesses([]);

    if (newEvidenceType === "action_item") {
      setLoadingLinked(true);
      listTakeaways(retryFetch, { type: "action" })
        .then(res => setActionItems(res.list || []))
        .catch(() => {})
        .finally(() => setLoadingLinked(false));
    } else if (newEvidenceType === "insight") {
      setLoadingLinked(true);
      listTakeaways(retryFetch, { type: "insight" })
        .then(res => setInsights(res.list || []))
        .catch(() => {})
        .finally(() => setLoadingLinked(false));
    } else if (newEvidenceType === "engagement" && user?.email) {
      setLoadingLinked(true);
      getUserPrescriptions(retryFetch, user.email, 500)
        .then(res => setPrescriptions(res.data ?? res.prescriptions ?? []))
        .catch(() => {})
        .finally(() => setLoadingLinked(false));
    }
  }, [newEvidenceType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch processes when prescription is selected
  useEffect(() => {
    if (!selectedPrescriptionId) return;
    setLoadingProcesses(true);
    setSelectedProcessId("");
    retryFetch(`${config.API_BASE}/api/processes?fiPrescription=${selectedPrescriptionId}&limit=50`, {
      credentials: "include",
    })
      .then((r: Response) => r.json())
      .then((data: any) => setLinkedProcesses(data.processes ?? data.data ?? []))
      .catch(() => {})
      .finally(() => setLoadingProcesses(false));
  }, [selectedPrescriptionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save helpers ────────────────────────────────────────────────────────

  const handleSaveName = useCallback(async () => {
    if (!trait || !nameDraft.trim()) return;
    setSaving(true);
    try {
      await onUpdate(trait._id, { name: nameDraft.trim() });
      setEditingName(false);
    } catch {
      toast.error(t("trait_save_error", "Failed to save"));
    } finally {
      setSaving(false);
    }
  }, [trait, nameDraft, onUpdate, t]);

  const handleBlurDefinition = useCallback(async () => {
    if (!trait || readOnly) return;
    if (definitionDraft.trim() !== (trait.personalDefinition || "").trim()) {
      try {
        await onUpdate(trait._id, {
          personalDefinition: definitionDraft.trim(),
        });
      } catch {
        toast.error(t("trait_save_error", "Failed to save"));
      }
    }
  }, [trait, definitionDraft, readOnly, onUpdate, t]);

  const handleBlurOpportunities = useCallback(async () => {
    if (!trait || readOnly) return;
    if (
      opportunitiesDraft.trim() !==
      (trait.developmentOpportunities || "").trim()
    ) {
      try {
        await onUpdate(trait._id, {
          developmentOpportunities: opportunitiesDraft.trim(),
        });
      } catch {
        toast.error(t("trait_save_error", "Failed to save"));
      }
    }
  }, [trait, opportunitiesDraft, readOnly, onUpdate, t]);

  // Pre-fetch level descriptions for all non-aspiration levels when the trait opens
  useEffect(() => {
    if (!trait) return;
    setLevelDescriptions({});
    setLoadingLevelDescs(true);
    calibrateTraitBatch(retryFetch, { traitId: trait._id, traitName: trait.name, language: i18n.language })
      .then((results) => {
        const descs: Record<number, string> = {};
        Object.entries(results).forEach(([lvl, res]) => {
          if (res.question) descs[Number(lvl)] = res.question;
        });
        setLevelDescriptions(descs);
      })
      .catch(() => {})
      .finally(() => setLoadingLevelDescs(false));
  }, [trait?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssessment = useCallback(
    async (level: number) => {
      if (!trait || readOnly) return;
      try {
        await onAssess(trait._id, level);
      } catch {
        toast.error(t("trait_assess_error", "Failed to assess"));
      }
    },
    [trait, readOnly, onAssess, t]
  );

  const handleAssistDefinition = useCallback(async () => {
    if (!trait) return;
    setDefAssisting(true);
    try {
      const result = await assistTraitDefinition(retryFetch, {
        traitId: trait._id,
        traitName: trait.name,
        category: trait.category,
        currentDefinition: definitionDraft || undefined,
        level: trait.level,
      });
      setDefinitionDraft(result.suggestion);
      toast.success(t("trait_definition_assisted", "Definition updated — review and save"));
    } catch {
      toast.error(t("trait_definition_assist_error", "Could not get AI suggestion"));
    } finally {
      setDefAssisting(false);
    }
  }, [trait, definitionDraft, retryFetch, t]);

  const handleAddExample = useCallback(async () => {
    if (!trait || !newExample.trim()) return;
    const updated = [...examplesDraft, newExample.trim()];
    setExamplesDraft(updated);
    setNewExample("");
    try {
      await onUpdate(trait._id, { personalExamples: updated });
    } catch {
      toast.error(t("trait_save_error", "Failed to save"));
    }
  }, [trait, newExample, examplesDraft, onUpdate, t]);

  const handleRemoveExample = useCallback(
    async (index: number) => {
      if (!trait || readOnly) return;
      const updated = examplesDraft.filter((_, i) => i !== index);
      setExamplesDraft(updated);
      try {
        await onUpdate(trait._id, { personalExamples: updated });
      } catch {
        toast.error(t("trait_save_error", "Failed to save"));
      }
    },
    [trait, examplesDraft, readOnly, onUpdate, t]
  );

  const handleAddTag = useCallback(async () => {
    if (!trait || !newTag.trim()) return;
    if (tagsDraft.includes(newTag.trim())) {
      setNewTag("");
      return;
    }
    const updated = [...tagsDraft, newTag.trim()];
    setTagsDraft(updated);
    setNewTag("");
    try {
      await onUpdate(trait._id, { tags: updated });
    } catch {
      toast.error(t("trait_save_error", "Failed to save"));
    }
  }, [trait, newTag, tagsDraft, onUpdate, t]);

  const handleRemoveTag = useCallback(
    async (tag: string) => {
      if (!trait || readOnly) return;
      const updated = tagsDraft.filter((t) => t !== tag);
      setTagsDraft(updated);
      try {
        await onUpdate(trait._id, { tags: updated });
      } catch {
        toast.error(t("trait_save_error", "Failed to save"));
      }
    },
    [trait, tagsDraft, readOnly, onUpdate, t]
  );

  const handleDeleteTrait = useCallback(async () => {
    if (!trait || !onDelete) return;
    try {
      await onDelete(trait._id);
      onClose();
      toast.success(t("trait_deleted", "Trait deleted"));
    } catch {
      toast.error(t("trait_delete_error", "Failed to delete trait"));
    }
  }, [trait, onDelete, onClose, t]);

  if (!trait) return null;

  const levelConfig = getLevelConfig(trait.level);
  const HierarchyIcon = trait.hierarchy
    ? HIERARCHY_ICON_MAP[trait.hierarchy]
    : null;
  const categoryStyle = CATEGORY_STYLES[trait.category] || CATEGORY_STYLES.strength;
  const evidenceCount = countEvidence(trait);
  const crossSpaces =
    spaces && trait.spaceIds.length > 1
      ? spaces.filter(
          (s) => trait.spaceIds.includes(s._id)
        )
      : [];

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col"
      >
        <ScrollArea className="flex-1">
          <div className="p-6 space-y-6">
            {/* ── Header ───────────────────────────────────────────── */}
            <SheetHeader className="space-y-3">
              <div className="flex items-start gap-3">
                {/* Icon */}
                <span className="shrink-0 mt-0.5 flex items-center">
                  <TraitIcon value={trait.emoji} fallback={Circle} className="h-6 w-6" />
                </span>

                <div className="flex-1 min-w-0">
                  {/* Editable name */}
                  {editingName && !readOnly ? (
                    <div className="flex items-center gap-1.5">
                      <Input
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        className="h-8 text-base font-semibold"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveName();
                          if (e.key === "Escape") setEditingName(false);
                        }}
                      />
                      <button
                        onClick={handleSaveName}
                        disabled={saving}
                        className="p-1 rounded hover:bg-gray-100 text-emerald-600"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingName(false);
                          setNameDraft(trait.name);
                        }}
                        className="p-1 rounded hover:bg-gray-100 text-gray-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <SheetTitle className="flex items-center gap-2">
                      <span className="truncate">{trait.name}</span>
                      {!readOnly && (
                        <button
                          onClick={() => setEditingName(true)}
                          className="p-1 rounded text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-colors shrink-0"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </SheetTitle>
                  )}

                  {/* Badges row */}
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    {/* Hierarchy badge */}
                    {trait.hierarchy && HierarchyIcon && (
                      <Badge
                        variant="outline"
                        className="text-[10px] gap-1 py-0 px-1.5"
                      >
                        <HierarchyIcon className="w-3 h-3" />
                        {getHierarchyLabel(trait.hierarchy, t)}
                      </Badge>
                    )}
                    {/* Category badge */}
                    <Badge
                      className={cn(
                        "text-[10px] py-0 px-1.5 border-0",
                        categoryStyle.bg,
                        categoryStyle.text
                      )}
                    >
                      {t(`trait_category_${trait.category}`, trait.category)}
                    </Badge>
                    {/* Source badge */}
                    {trait.source && trait.source !== "user_created" && (
                      <Badge
                        variant="outline"
                        className="text-[10px] py-0 px-1.5"
                      >
                        {t(`trait_source_${trait.source}`, trait.source)}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <SheetDescription className="sr-only">
                {t("trait_detail_description", "View and edit trait details")}
              </SheetDescription>
            </SheetHeader>

            <Separator />

            {/* ── Assessment Level ──────────────────────────────────── */}
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                {t("trait_assessment", "Assessment")}
              </h4>
              <AssessmentLevelInput
                value={trait.level}
                onChange={handleAssessment}
                size="md"
                showLabels
                disabled={readOnly}
                accentColor={spaceColor}
                levelDescriptions={levelDescriptions}
                loadingDescriptions={loadingLevelDescs}
              />
              {/* Selected-level description */}
              {trait.level >= 0 && (
                <div className="mt-3 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2.5">
                  {loadingLevelDescs && !levelDescriptions[trait.level] ? (
                    <div className="flex items-center gap-2 text-xs text-indigo-500">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {t("trait_calibrating", "Generating level descriptions…")}
                    </div>
                  ) : levelDescriptions[trait.level] ? (
                    <div className="flex items-start gap-2">
                      <Wand2 className="w-3.5 h-3.5 text-indigo-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-indigo-700 leading-relaxed">
                        {levelDescriptions[trait.level]}
                      </p>
                    </div>
                  ) : null}
                </div>
              )}
            </section>

            <Separator />

            {/* ── Personal Definition ──────────────────────────────── */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {t("trait_personal_definition", "Personal Definition")}
                </h4>
                {!readOnly && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={handleAssistDefinition}
                          disabled={defAssisting}
                          className="flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-600 transition-colors disabled:opacity-50"
                        >
                          {defAssisting ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Wand2 className="w-3 h-3" />
                          )}
                          {t("trait_ai_assist", "AI assist")}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p className="text-xs">
                          {t("trait_ai_assist_tooltip", "Let AI help write your personal definition")}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {readOnly ? (
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {definitionDraft ||
                    t("trait_no_definition", "No definition yet")}
                </p>
              ) : (
                <Textarea
                  value={definitionDraft}
                  onChange={(e) => setDefinitionDraft(e.target.value)}
                  onBlur={handleBlurDefinition}
                  placeholder={t(
                    "trait_personal_definition_placeholder",
                    "In your own words, what does this trait mean in your life?"
                  )}
                  className="min-h-[80px] resize-none text-sm"
                />
              )}
            </section>

            <Separator />

            {/* ── Personal Examples ─────────────────────────────────── */}
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                {t("trait_personal_examples", "Personal Examples")}
              </h4>
              <div className="space-y-2">
                {examplesDraft.map((ex, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 group text-sm bg-gray-50 rounded-lg px-3 py-2"
                  >
                    <span className="flex-1 text-gray-700">{ex}</span>
                    {!readOnly && (
                      <button
                        onClick={() => handleRemoveExample(i)}
                        className="p-0.5 rounded text-gray-300 opacity-0 group-hover:opacity-100 hover:text-red-400 transition-all shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                {!readOnly && (
                  <div className="flex items-center gap-2">
                    <Input
                      value={newExample}
                      onChange={(e) => setNewExample(e.target.value)}
                      placeholder={t(
                        "trait_add_example",
                        "Add an example..."
                      )}
                      className="h-8 text-sm flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddExample();
                        }
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleAddExample}
                      disabled={!newExample.trim()}
                      className="h-8 px-2"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                {examplesDraft.length === 0 && readOnly && (
                  <p className="text-sm text-gray-400 italic">
                    {t("trait_no_examples", "No examples yet")}
                  </p>
                )}
              </div>
            </section>

            <Separator />

            {/* ── Development Opportunities ─────────────────────────── */}
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                {t("trait_development_opportunities", "Development Opportunities")}
              </h4>
              {readOnly ? (
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {opportunitiesDraft ||
                    t("trait_no_opportunities", "None yet")}
                </p>
              ) : (
                <Textarea
                  value={opportunitiesDraft}
                  onChange={(e) => setOpportunitiesDraft(e.target.value)}
                  onBlur={handleBlurOpportunities}
                  placeholder={t(
                    "trait_development_opportunities_placeholder",
                    "What opportunities do you see to grow this trait?"
                  )}
                  className="min-h-[60px] resize-none text-sm"
                />
              )}
            </section>

            <Separator />

            {/* ── Evidence ──────────────────────────────────────────── */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {t("trait_evidence", "Evidence")}
                </h4>
                {evidenceCount > 0 && (
                  <Badge variant="secondary" className="text-[10px] py-0">
                    {evidenceCount}
                  </Badge>
                )}
              </div>
              <div className="space-y-1.5">
                {trait.evidence && trait.evidence.length > 0 ? (
                  trait.evidence.map((ev: any, i: number) => {
                    const EvidenceIcon = EVIDENCE_ICONS[ev.type] || FileText;
                    return (
                      <div
                        key={ev._id ?? i}
                        className="flex items-start gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2 group"
                      >
                        <EvidenceIcon className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          {ev.note && (
                            <p className="text-gray-700 text-sm">{ev.note}</p>
                          )}
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-gray-400 capitalize">
                              {t(`trait_evidence_${ev.type}`, ev.type.replace("_", " "))}
                            </span>
                            {ev.relationship && (
                              <span className="text-[10px] text-gray-300">
                                {t(`trait_evidence_rel_${ev.relationship}`, ev.relationship)}
                              </span>
                            )}
                            <span className="text-[10px] text-gray-300">
                              {new Date(ev.date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        {!readOnly && onDeleteEvidence && ev._id && (
                          <button
                            onClick={() => onDeleteEvidence(trait._id, ev._id)}
                            className="p-0.5 rounded text-gray-300 opacity-0 group-hover:opacity-100 hover:text-red-400 transition-all shrink-0"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-400 italic">
                    {t("trait_no_evidence", "No evidence linked yet")}
                  </p>
                )}

                {/* Add evidence form */}
                {!readOnly && onAddEvidence && (
                  <div className="space-y-2 pt-1">
                    <div className="flex gap-2">
                      <select
                        value={newEvidenceType}
                        onChange={e => setNewEvidenceType(e.target.value as AddEvidenceRequest["type"])}
                        className="h-8 text-xs rounded-md border border-input bg-background px-2 flex-1"
                      >
                        {/* <option value="note">Note</option> */}
                        <option value="insight">Insight</option>
                        <option value="action_item">Action Item</option>
                        <option value="engagement">Engagement</option>
                      </select>
                      <select
                        value={newEvidenceRel}
                        onChange={e => setNewEvidenceRel(e.target.value as EvidenceRelationship)}
                        className="h-8 text-xs rounded-md border border-input bg-background px-2 flex-1"
                      >
                        <option value="develops">Develops</option>
                        <option value="demonstrates">Demonstrates</option>
                        <option value="challenges">Challenges</option>
                      </select>
                    </div>

                    {/* Insight picker */}
                    {newEvidenceType === "insight" && (
                      <Popover open={openInsightPicker} onOpenChange={setOpenInsightPicker} modal>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="w-full justify-between h-8 text-xs font-normal">
                            {selectedInsightId
                              ? (insights.find(i => i._id === selectedInsightId)?.content || "").replace(/<[^>]*>/g, "").slice(0, 60)
                              : loadingLinked ? "Loading..." : "Select insight..."}
                            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0 ml-2" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t("search_insights", "Search insights...")} className="h-8 text-xs" />
                            <CommandList>
                              <CommandEmpty>No insights found.</CommandEmpty>
                              {insights.map((ins: UserTakeaway) => {
                                const label = (ins.content || "").replace(/<[^>]*>/g, "").slice(0, 80);
                                return (
                                  <CommandItem key={ins._id} value={label} onSelect={() => { setSelectedInsightId(ins._id); setOpenInsightPicker(false); }} className="text-xs">
                                    {label}
                                  </CommandItem>
                                );
                              })}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}

                    {/* Action item picker */}
                    {newEvidenceType === "action_item" && (
                      <Popover open={openActionPicker} onOpenChange={setOpenActionPicker} modal>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="w-full justify-between h-8 text-xs font-normal">
                            {selectedActionItemId
                              ? (actionItems.find(a => a._id === selectedActionItemId)?.content || "").replace(/<[^>]*>/g, "").slice(0, 60)
                              : loadingLinked ? "Loading..." : "Select action item..."}
                            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0 ml-2" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t("search_action_items", "Search action items...")} className="h-8 text-xs" />
                            <CommandList>
                              <CommandEmpty>No action items found.</CommandEmpty>
                              {actionItems.map((a: UserTakeaway) => {
                                const label = (a.content || "").replace(/<[^>]*>/g, "").slice(0, 80) + (a.status === "completed" ? " ✓" : "");
                                return (
                                  <CommandItem key={a._id} value={label} onSelect={() => { setSelectedActionItemId(a._id); setOpenActionPicker(false); }} className="text-xs">
                                    {label}
                                  </CommandItem>
                                );
                              })}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}

                    {/* Engagement: step 1 — pick activity */}
                    {newEvidenceType === "engagement" && (
                      <Popover open={openActivityPicker} onOpenChange={setOpenActivityPicker} modal>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="w-full justify-between h-8 text-xs font-normal">
                            {selectedPrescriptionId
                              ? (prescriptions.find((p: any) => p._id === selectedPrescriptionId) as any)?.name
                              : loadingLinked ? "Loading..." : "Select activity..."}
                            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0 ml-2" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t("trait_search_activities", "Search activities...")} className="h-8 text-xs" />
                            <CommandList>
                              <CommandEmpty>No activities found.</CommandEmpty>
                              {prescriptions.map((p: any) => (
                                <CommandItem key={p._id} value={p.name} onSelect={() => { setSelectedPrescriptionId(p._id); setOpenActivityPicker(false); }} className="text-xs">
                                  {p.name}
                                </CommandItem>
                              ))}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}

                    {/* Engagement: step 2 — pick engagement */}
                    {newEvidenceType === "engagement" && selectedPrescriptionId && (
                      <Popover open={openEngagementPicker} onOpenChange={setOpenEngagementPicker} modal>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="w-full justify-between h-8 text-xs font-normal">
                            {selectedProcessId
                              ? (() => { const p = linkedProcesses.find((p: any) => p._id === selectedProcessId) as any; return p ? (p.engagementName || p.prescriptionName) : ""; })()
                              : loadingProcesses ? "Loading..." : "Select engagement..."}
                            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0 ml-2" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t("trait_search_engagements", "Search engagements...")} className="h-8 text-xs" />
                            <CommandList>
                              <CommandEmpty>No engagements found.</CommandEmpty>
                              {linkedProcesses.map((p: any) => {
                                const label = (p.engagementName || p.prescriptionName) + ` (${p.status})`;
                                return (
                                  <CommandItem key={p._id} value={label} onSelect={() => { setSelectedProcessId(p._id); setOpenEngagementPicker(false); }} className="text-xs">
                                    {label}
                                  </CommandItem>
                                );
                              })}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}

                    <div className="flex items-center gap-2">
                      <Input
                        value={newEvidenceNote}
                        onChange={e => setNewEvidenceNote(e.target.value)}
                        placeholder={t("trait_add_evidence", "Describe the evidence...")}
                        className="h-8 text-sm flex-1"
                        onKeyDown={async e => {
                          if (e.key === "Enter" && !addingEvidence) {
                            e.preventDefault();
                            const referenceId =
                              newEvidenceType === "action_item" ? selectedActionItemId || undefined :
                              newEvidenceType === "insight" ? selectedInsightId || undefined :
                              newEvidenceType === "engagement" ? selectedProcessId || undefined :
                              undefined;
                            if (!newEvidenceNote.trim() && !referenceId) return;
                            setAddingEvidence(true);
                            try {
                              await onAddEvidence(trait._id, { type: newEvidenceType, relationship: newEvidenceRel, note: newEvidenceNote.trim() || undefined, referenceId, linkedBy: "user" });
                              setNewEvidenceNote("");
                              setSelectedActionItemId(""); setSelectedInsightId(""); setSelectedPrescriptionId(""); setSelectedProcessId(""); setLinkedProcesses([]);
                            } catch { toast.error(t("trait_save_error", "Failed to save")); }
                            finally { setAddingEvidence(false); }
                          }
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                        disabled={addingEvidence || (!newEvidenceNote.trim() && !selectedActionItemId && !selectedInsightId && !selectedProcessId)}
                        onClick={async () => {
                          if (addingEvidence) return;
                          const referenceId =
                            newEvidenceType === "action_item" ? selectedActionItemId || undefined :
                            newEvidenceType === "insight" ? selectedInsightId || undefined :
                            newEvidenceType === "engagement" ? selectedProcessId || undefined :
                            undefined;
                          if (!newEvidenceNote.trim() && !referenceId) return;
                          setAddingEvidence(true);
                          try {
                            await onAddEvidence(trait._id, { type: newEvidenceType, relationship: newEvidenceRel, note: newEvidenceNote.trim() || undefined, referenceId, linkedBy: "user" });
                            setNewEvidenceNote("");
                            setSelectedActionItemId(""); setSelectedInsightId(""); setSelectedPrescriptionId(""); setSelectedProcessId(""); setLinkedProcesses([]);
                          } catch { toast.error(t("trait_save_error", "Failed to save")); }
                          finally { setAddingEvidence(false); }
                        }}
                      >
                        {addingEvidence ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* ── Cross-identity ────────────────────────────────────── */}
            {crossSpaces.length > 1 && (
              <>
                <Separator />
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                    {t("trait_cross_identity", "Also active in")}
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {crossSpaces.map((s) => (
                      <Badge
                        key={s._id}
                        variant="outline"
                        className="text-xs gap-1"
                      >
                        <Orbit className="w-3 h-3" />
                        {s.name}
                      </Badge>
                    ))}
                  </div>
                </section>
              </>
            )}

            {/* ── Tags ─────────────────────────────────────────────── */}
            <Separator />
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                <Tag className="w-3 h-3 inline-block me-1" />
                {t("trait_tags", "Tags")}
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {tagsDraft.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="text-xs gap-1 group"
                  >
                    {tag}
                    {!readOnly && (
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </Badge>
                ))}
                {!readOnly && (
                  <div className="flex items-center gap-1">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder={t("trait_add_tag", "Add tag...")}
                      className="h-6 w-24 text-xs px-2"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                    />
                  </div>
                )}
                {tagsDraft.length === 0 && readOnly && (
                  <span className="text-xs text-gray-400 italic">
                    {t("trait_no_tags", "No tags")}
                  </span>
                )}
              </div>
            </section>

            {/* ── Find Activities ──────────────────────────────────── */}
            <Separator />
            <section>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-indigo-600 border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
                      onClick={() => {
                        const params = new URLSearchParams({
                          q: trait.name,
                          traitId: trait._id,
                          tab: "activities",
                        });
                        // The identity-space id lets the marketplace run a
                        // trait-focused semantic search instead of a plain
                        // text search on the trait name.
                        const sid = spaceId || trait.spaceIds?.[0];
                        if (sid) params.set("spaceId", sid);
                        window.location.href = `/marketplace?${params.toString()}`;
                      }}
                    >
                      <Search className="w-4 h-4 me-2" />
                      {t("trait_find_activities", "Find activities for this trait")}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">
                      {t("trait_find_activities_tooltip", "Search the marketplace for activities that develop this trait")}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </section>

            {/* ── Work on it in Mental Models ─────────────────────── */}
            {!readOnly && trait?._id && !String(trait._id).startsWith("new_") && (
              <section>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-primary hover:bg-primary/5"
                  onClick={async () => {
                    try {
                      const res = await liftEntity(retryFetch, {
                        entityType: "user_trait",
                        entityId: trait._id,
                      });
                      sessionStorage.setItem("mm_open_object", res.object._id);
                      localStorage.setItem("workspace_active_tab", "mental_models");
                      window.dispatchEvent(
                        new CustomEvent("workspace-set-tab", { detail: "mental_models" })
                      );
                      window.location.href = "/portal";
                    } catch (e) {
                      console.error("trait lift failed:", e);
                    }
                  }}
                >
                  <Brain className="w-4 h-4 me-2" />
                  {t("mm_lift_action", "Work on this in Mental Models")}
                </Button>
              </section>
            )}

            {/* ── Delete ───────────────────────────────────────────── */}
            {!readOnly && onDelete && (
              <>
                <Separator />
                <section>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 me-2" />
                        {t("trait_delete", "Delete trait")}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {t("trait_delete_confirm_title", "Delete trait?")}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {t(
                            "trait_delete_confirm_message",
                            "This will permanently delete this trait and all its children. This action cannot be undone."
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>
                          {t("cancel", "Cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteTrait}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          {t("delete", "Delete")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </section>
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default TraitDetailPanel;
